import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthService } from "@/hooks/services/authService";
import {
  Mail,
  Lock,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import { AuthStore } from "@/hooks/stores/AuthStore";

// ========== STUDENT FORM COMPONENT ==========
const StudentSignInForm = ({ 
  formData, 
  handleChange, 
  isLoading, 
  success,
  showPassword,
  setShowPassword,
  isSwitching
}) => (
  <>
    {/* Email */}
    <div className="space-y-2">
      <label className="text-sm font-semibold text-slate-700">
        Student Email
      </label>
      <div className="relative">
        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="email"
          name="email"
          placeholder="student@email.com"
          value={formData.email}
          onChange={handleChange}
          disabled={isLoading || success || isSwitching}
          className="w-full pl-12 pr-4 py-3 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm focus:bg-white/90 focus:border-blue-400 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
        />
      </div>
    </div>

    {/* Password */}
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <label className="text-sm font-semibold text-slate-700">
          Password
        </label>
        <a
          href="#"
          className="text-xs text-blue-600 hover:text-blue-700 font-semibold"
        >
          Forgot?
        </a>
      </div>
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="••••••••"
          value={formData.password}
          onChange={handleChange}
          disabled={isLoading || success || isSwitching}
          className="w-full pl-12 pr-12 py-3 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm focus:bg-white/90 focus:border-blue-400 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          disabled={isLoading || success || isSwitching}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 disabled:hover:text-slate-400"
        >
          {showPassword ? (
            <EyeOff className="w-5 h-5" />
          ) : (
            <Eye className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  </>
);

// ========== SCHOOL FORM COMPONENT ==========
const SchoolSignInForm = ({ 
  formData, 
  handleChange, 
  isLoading, 
  success,
  showPassword,
  setShowPassword,
  isSwitching
}) => (
  <>
    {/* Email */}
    <div className="space-y-2">
      <label className="text-sm font-semibold text-slate-700">
        School Email
      </label>
      <div className="relative">
        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="email"
          name="email"
          placeholder="school@email.com"
          value={formData.email}
          onChange={handleChange}
          disabled={isLoading || success || isSwitching}
          className="w-full pl-12 pr-4 py-3 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm focus:bg-white/90 focus:border-teal-400 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
        />
      </div>
    </div>

    {/* Password */}
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <label className="text-sm font-semibold text-slate-700">
          Password
        </label>
        <a
          href="#"
          className="text-xs text-teal-600 hover:text-teal-700 font-semibold"
        >
          Forgot?
        </a>
      </div>
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="••••••••"
          value={formData.password}
          onChange={handleChange}
          disabled={isLoading || success || isSwitching}
          className="w-full pl-12 pr-12 py-3 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm focus:bg-white/90 focus:border-teal-400 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          disabled={isLoading || success || isSwitching}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 disabled:hover:text-slate-400"
        >
          {showPassword ? (
            <EyeOff className="w-5 h-5" />
          ) : (
            <Eye className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  </>
);

// ========== MAIN SIGNIN PAGE ==========
const SignInPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("student");
  const [formData, setFormData] = useState({
    email: "demo@example.com",
    password: "123456",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleTabSwitch = (tab) => {
    if (tab === activeTab || isSwitching) return;

    setIsSwitching(true);
    setActiveTab(tab);

    // Reset form when switching tabs
    setFormData({ email: "", password: "" });
    setError("");

    // Enable interaction after animation completes
    setTimeout(() => setIsSwitching(false), 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try{
      const user = await AuthService.login({
        email: formData.email,
        password: formData.password
      })

      AuthStore.getState().login(true, activeTab, user)

      setSuccess(true);
      setTimeout(() => {
        navigate(
          activeTab === "student" ? "/dashboard" : "/school-dashboard",
        )
      }, 1500)
    } catch(err){
      setError(err.message || 'login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl"></div>
      </div>

      {/* Layout container with side swap based on tab */}
      <div className="w-full flex flex-col lg:flex-row relative z-10">
        {/* Image Section - Swaps sides based on tab */}
        <div className="hidden lg:flex w-1/2 items-center justify-center p-8 overflow-hidden">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Student Image with sliding animation */}
            <div
              className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ease-in-out ${
                activeTab === "student"
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-full"
              }`}
            >
              <div className="flex flex-col items-center justify-center space-y-6 max-w-lg">
                <div className="relative w-full max-w-md">
                  <img
                    src="/students.avif"
                    alt="Team Player"
                    className="w-full h-auto rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full font-bold text-lg shadow-lg">
                    Student Access
                  </div>
                </div>
              </div>
            </div>

            {/* School Image with sliding animation */}
            <div
              className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ease-in-out ${
                activeTab === "school"
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-full"
              }`}
            >
              <div className="flex flex-col items-center justify-center space-y-6 max-w-lg">
                <div className="relative w-full max-w-md">
                  <img
                    src="/schools.avif"
                    alt="Space Explorer"
                    className="w-full h-auto rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-teal-500 to-green-600 text-white px-6 py-2 rounded-full font-bold text-lg shadow-lg">
                    School Portal
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section - Swaps sides based on tab */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 overflow-hidden">
          <div className="w-full max-w-sm space-y-6">
            {/* Tab Switch */}
            <div className="flex gap-3 bg-white/30 backdrop-blur-sm p-1 rounded-xl border border-white/60 shadow-sm">
              <button
                onClick={() => handleTabSwitch("student")}
                disabled={isSwitching}
                className={`flex-1 py-2.5 px-4 rounded-lg font-semibold transition-all duration-300 ${
                  activeTab === "student"
                    ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                    : "text-slate-700 hover:bg-white/20 disabled:hover:bg-transparent"
                } disabled:opacity-50`}
              >
                Student
              </button>
              <button
                onClick={() => handleTabSwitch("school")}
                disabled={isSwitching}
                className={`flex-1 py-2.5 px-4 rounded-lg font-semibold transition-all duration-300 ${
                  activeTab === "school"
                    ? "bg-gradient-to-r from-teal-500 to-green-600 text-white shadow-lg"
                    : "text-slate-700 hover:bg-white/20 disabled:hover:bg-transparent"
                } disabled:opacity-50`}
              >
                School
              </button>
            </div>

            {/* Header with fade animation */}
            <div
              className={`text-center space-y-2 transition-all duration-300 ${isSwitching ? "opacity-0" : "opacity-100"}`}
            >
              <h1 className="text-3xl font-bold text-slate-900">Sign In</h1>
              <p className="text-slate-600">Access your {activeTab} account</p>
            </div>

            {/* Glassy Card */}
            <div
              className={`backdrop-blur-xl bg-white/40 border border-white/60 rounded-2xl p-8 shadow-2xl transition-all duration-300 ${isSwitching ? "opacity-0 scale-95" : "opacity-100 scale-100"}`}
            >
              {success && (
                <div className="mb-6 p-4 bg-green-50/80 backdrop-blur-sm border border-green-200/50 rounded-lg flex items-center gap-3 animate-in">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-green-800 font-medium">
                    Signing in...
                  </span>
                </div>
              )}

              {error && !success && (
                <div className="mb-6 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200/50 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-red-800 font-medium">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Conditional Form Rendering */}
                {activeTab === "student" ? (
                  <StudentSignInForm 
                    formData={formData}
                    handleChange={handleChange}
                    isLoading={isLoading}
                    success={success}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    isSwitching={isSwitching}
                  />
                ) : (
                  <SchoolSignInForm 
                    formData={formData}
                    handleChange={handleChange}
                    isLoading={isLoading}
                    success={success}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    isSwitching={isSwitching}
                  />
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || success || isSwitching}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all shadow-lg mt-6 ${
                    activeTab === "student"
                      ? "bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
                      : "bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600"
                  } disabled:opacity-50`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Signing In...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      Sign In
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-white/40"></div>
                <span className="text-xs text-slate-500">No account?</span>
                <div className="flex-1 h-px bg-white/40"></div>
              </div>

              {/* Sign Up Link */}
              <button
                type="button"
                onClick={() => navigate("/")}
                className="block w-full py-3 px-4 bg-white/30 hover:bg-white/50 border border-white/60 rounded-lg text-slate-700 font-semibold transition-all text-center"
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SignInPage;